import React, { useState } from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { AlertCircle, CheckCircle } from 'lucide-react';

const AccessibleForm = ({ 
  children, 
  onSubmit, 
  className = '',
  ariaLabel,
  ariaDescribedBy,
  ...props 
}) => {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (formData) => {
    const newErrors = {};
    // Add validation logic here as needed
    // Example: if (!formData.get('email')) newErrors.email = 'Email is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formData = new FormData(e.target);
      const validationErrors = validateForm(formData);
      
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }
      
      setErrors({});
      await onSubmit?.(e);
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ submit: 'An error occurred while submitting the form' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-4 ${className}`}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      noValidate
      {...props}
    >
      {children}
      
      {/* Live region for form status announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {isSubmitting && "Form is being submitted"}
        {Object.keys(errors).length > 0 && `Form has ${Object.keys(errors).length} errors: ${Object.values(errors).join('. ')}`}
      </div>
    </form>
  );
};

const AccessibleFormField = ({
  label,
  id,
  error,
  required = false,
  description,
  children,
  className = ''
}) => {
  const errorId = error ? `${id}-error` : undefined;
  const descriptionId = description ? `${id}-description` : undefined;
  const ariaDescribedBy = [errorId, descriptionId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={`space-y-2 ${className}`}>
      <Label 
        htmlFor={id}
        className={`block text-sm font-medium ${error ? 'text-red-700' : 'text-gray-700'}`}
      >
        {label}
        {required && (
          <span className="text-red-500 ml-1" aria-label="required">
            *
          </span>
        )}
      </Label>
      
      {description && (
        <p id={descriptionId} className="text-sm text-gray-600">
          {description}
        </p>
      )}
      
      <div className="relative">
        {React.isValidElement(children) ? (
          React.cloneElement(children, {
            ...children.props,
            id,
            'aria-describedby': ariaDescribedBy,
            'aria-invalid': error ? 'true' : undefined,
            'aria-required': required ? 'true' : undefined,
            className: [
              children.props.className,
              error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : null
            ].filter(Boolean).join(' ')
          })
        ) : (
          <span className="text-gray-500">Invalid form element</span>
        )}
        
        {error && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <AlertCircle className="h-5 w-5 text-red-500" aria-hidden="true" />
          </div>
        )}
      </div>
      
      {error && (
        <p id={errorId} className="text-sm text-red-600 flex items-center">
          <AlertCircle className="h-4 w-4 mr-1" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
};

const AccessibleButton = ({
  children,
  loading = false,
  loadingText = 'Loading...',
  variant = 'default',
  size = 'default',
  className = '',
  ariaLabel,
  ariaDescribedBy,
  ...props
}) => {
  return (
    <Button
      className={`focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 ${className}`}
      variant={variant}
      size={size}
      disabled={loading || props.disabled}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-busy={loading ? 'true' : 'false'}
      {...props}
    >
      {loading ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" aria-hidden="true" />
          {loadingText}
        </>
      ) : (
        children
      )}
    </Button>
  );
};

const SkipLink = ({ href = '#main-content', children = 'Skip to main content' }) => {
  return (
    <a href={href} className="skip-link">
      {children}
    </a>
  );
};

const LiveRegion = ({ 
  message, 
  level = 'polite', 
  atomic = true,
  className = '' 
}) => {
  return (
    <div
      aria-live={level}
      aria-atomic={atomic}
      className={className}
    >
      {message}
    </div>
  );
};

export {
  AccessibleForm,
  AccessibleFormField,
  AccessibleButton,
  SkipLink,
  LiveRegion
};